import {useEffect, useRef} from "react";

declare global {
    interface Window {
        gtag: (...args: any[]) => void;
        dataLayer: any[];
    }
}

export const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID || '';

const CLIENT_ID_STORAGE_KEY = 'pluginsdb-ga-client-id';

const isBrowser = typeof window !== 'undefined';

const ensureClientId = (): string | null => {
    if (!isBrowser) {
        return null;
    }

    try {
        const cached = window.localStorage.getItem(CLIENT_ID_STORAGE_KEY);
        if (cached) {
            return cached;
        }

        const canUseCrypto = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function';
        const clientId = canUseCrypto
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random()}`;
        window.localStorage.setItem(CLIENT_ID_STORAGE_KEY, clientId);
        return clientId;
    } catch (error) {
        console.warn('Unable to access localStorage for analytics client id', error);
        return null;
    }
};

const sendToServer = async (name: string, params?: Record<string, any>) => {
    if (!isBrowser) {
        return;
    }

    try {
        await fetch('/api/analytics', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                name,
                params,
                clientId: ensureClientId(),
                pageLocation: window.location.href,
            }),
        });
    } catch (error) {
        console.warn('Analytics fallback failed', error);
    }
};

export const trackEvent = async (name: string, params: Record<string, any> = {}) => {
    if (!GA_MEASUREMENT_ID || !isBrowser) {
        return;
    }

    try {
        if (typeof window.gtag === 'function') {
            window.gtag('event', name, params);
            return;
        }
    } catch (error) {
        console.warn('gtag tracking failed, falling back to server', error);
    }

    await sendToServer(name, params);
};

export const trackPageView = async (url: string, title?: string) => {
    await trackEvent('page_view', {
        page_location: url,
        page_title: title || (isBrowser ? document.title : undefined),
        page_path: isBrowser ? window.location.pathname : undefined,
    });
};

export const useRouteAnalytics = (path: string) => {
    const previousPath = useRef<string | null>(null);

    useEffect(() => {
        if (!path || previousPath.current === path) {
            return;
        }

        previousPath.current = path;
        trackPageView(window.location.href);
    }, [path]);
};
